import { useEffect, useState, useRef } from "react";

import { Action, surveyLocalization } from "survey-core";
import "survey-core/survey-core.min.css";
import { ICreatorOptions } from "survey-creator-core";
import "survey-creator-core/survey-creator-core.min.css";
import { SurveyCreator, SurveyCreatorComponent } from "survey-creator-react";

// Ace Editor for JSON tab - provides line numbers, syntax highlighting, code folding
import "ace-builds/src-noconflict/ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-textmate";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/ext-searchbox";

import {
  AzureTranslationResponse,
  getSurveyTranslations,
} from "@rubi/api-generated";

import {
  SURVEYJS_SUPPORTED_LOCALES,
  SURVEYJS_LOCALE_NAMES,
  surveyJsLocaleToTranslateTarget,
} from "@/common/constants/languages";
import { defaultTheme } from "@/components/online-reporting/constants";
import { registerAutocomplete } from "@/components/online-reporting/custom-questions/AddressAutocompleteQuestion/AddressAutocompleteQuestion";
import { registerCameraRegistryMap } from "@/components/online-reporting/custom-questions/CameraRegistryQuestion/CameraRegistryQuestion";
import { registerCurrencyInput } from "@/components/online-reporting/custom-questions/CurrencyInputQuestion/CurrencyInputQuestion";
import { registerDriversLicenseScanner } from "@/components/online-reporting/custom-questions/DriversLicenseScannerQuestion";
import { registerEnhancedFileUpload } from "@/components/online-reporting/custom-questions/EnhancedFileUploadQuestion/EnhancedFileUploadQuestion";
import { registerGasQuantityInput } from "@/components/online-reporting/custom-questions/GasQuantityInputQuestion/GasQuantityInputQuestion";
import { registerInputTextAlignmentOverride } from "@/components/online-reporting/custom-questions/inputTextAlignmentOverride";
import { registerNarrativeCanvas } from "@/components/online-reporting/custom-questions/NarrativeCanvasQuestion/NarrativeCanvasQuestion";
import {
  registerShowInProperty,
  registerShowInVisualIndicators,
} from "@/components/online-reporting/custom-questions/showInProperty";
import { condensedViewTheme } from "@/components/online-reporting/utils";

import styles from "./FormBuilder.module.scss";
import { registerAddAllLanguagesItem } from "./registerAddAllLanguagesItem";
import { registerRowTranslateAction } from "./registerRowTranslateAction";

const FormBuilder = ({
  onSave,
  mode,
  surveyJSObject,
  isCondensedForm,
  supportedLanguages,
}: {
  onSave: (surveyJSObject) => Promise<void>;
  mode: "edit" | "create" | "view";
  surveyJSObject;
  isCondensedForm: boolean;
  supportedLanguages?: string[];
}) => {
  const creatorOptions: ICreatorOptions = {
    isAutoSave: true,
    readOnly: mode === "view",
    showPreviewTab: true,
    showDesignerTab: mode === "edit" || mode === "create",
    showJSONEditorTab: mode === "edit" || mode === "create",
    showLogicTab: mode === "edit" || mode === "create",
    showTranslationTab: mode === "edit" || mode === "create",
    showState: mode === "edit" || mode === "create",
  };
  const [creator, setCreator] = useState<SurveyCreator>(() => {
    const initialCreator = new SurveyCreator(creatorOptions);
    // Blue design surface from first paint — without a theme SurveyJS renders
    // its built-in teal until the effect below swaps in the configured creator.
    initialCreator.theme = defaultTheme;
    return initialCreator;
  });
  const [isCondensedPreview, setIsCondensedPreview] = useState(false);

  // Reset condensed preview state when form type changes
  useEffect(() => {
    // Check if we're currently on preview tab and it's a condensed form
    if (creator && isCondensedForm && creator.activeTab === "preview") {
      setIsCondensedPreview(true);
    } else {
      setIsCondensedPreview(false);
    }
  }, [isCondensedForm, creator]);

  // Use ref to always get latest onSave without recreating creator
  const onSaveRef = useRef(onSave);

  // Keep onSave ref up to date
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    registerAutocomplete();
    registerCameraRegistryMap();
    registerCurrencyInput();
    registerDriversLicenseScanner();
    registerEnhancedFileUpload();
    registerGasQuantityInput();
    registerNarrativeCanvas();
    registerShowInProperty();
    registerInputTextAlignmentOverride();

    const newCreator = new SurveyCreator(creatorOptions);

    // Theme the design surface (the survey being configured) so the editing
    // canvas renders in the blue defaultTheme instead of SurveyJS's built-in
    // teal. The condensed preview still re-themes itself in onPreviewSurveyCreated.
    newCreator.theme = defaultTheme;

    // Set up save function - use ref to always get latest onSave
    newCreator.saveSurveyFunc = function (saveNumber, callback) {
      const newSurveyContent = newCreator.text;
      const getUpdatedSurveyJSObject = () => {
        if (typeof newSurveyContent === "string") {
          return JSON.parse(newSurveyContent);
        } else {
          return newSurveyContent;
        }
      };

      const updatedSurveyJSObject = getUpdatedSurveyJSObject();

      onSaveRef
        .current(updatedSurveyJSObject)
        .then(() => {
          callback(saveNumber, true); // Indicate successful save
        })
        .catch((error) => {
          console.error("Error saving survey: ", error);
          callback(saveNumber, false); // Indicate unsuccessful save
        });
    };

    // Set up translation handler. SurveyJS waits on options.callback, so it
    // must be called on failure too — an empty array signals the failure.
    newCreator.onMachineTranslate.add(async (_, options) => {
      try {
        const data: { Text: string }[] = [];
        options.strings.forEach((str: string) => {
          data.push({ Text: str });
        });
        const targetLocale = surveyJsLocaleToTranslateTarget(
          options.toLocale,
          supportedLanguages,
        );
        const response = await getSurveyTranslations({
          body: {
            source_strings: data,
            target_language: targetLocale,
          },
        });
        if (!response.data) {
          options.callback([]);
          return;
        }
        const translatedStrings: string[] = [];
        response.data.translations.forEach((translation) => {
          translatedStrings.push(translation.translations[0].text);
        });
        options.callback(translatedStrings);
      } catch (error) {
        console.error(`Error translating to ${options.toLocale}:`, error);
        options.callback([]);
      }
    });

    // Update creator options if mode changes
    newCreator.readOnly = mode === "view";
    newCreator.showDesignerTab = mode === "edit" || mode === "create";
    newCreator.showJSONEditorTab = mode === "edit" || mode === "create";
    newCreator.showLogicTab = mode === "edit" || mode === "create";
    newCreator.showTranslationTab = mode === "edit" || mode === "create";

    // Map agency supported languages to SurveyJS locales and filter
    const mapToSurveyJSLocale = (langCode: string): string => {
      if (langCode === "enCa" || langCode === "enUs") return "en";
      if (langCode === "fr" || langCode === "frCa") return "fr";
      return langCode;
    };

    let filteredLocales = SURVEYJS_SUPPORTED_LOCALES;
    let filteredLocaleNames = SURVEYJS_LOCALE_NAMES;

    if (supportedLanguages && supportedLanguages.length > 0) {
      // Map agency languages to SurveyJS locales
      const mappedLocales = new Set(
        supportedLanguages
          .map(mapToSurveyJSLocale)
          .filter((locale) => SURVEYJS_SUPPORTED_LOCALES.includes(locale)),
      );

      // Always include "en" if any English variant is supported
      if (
        supportedLanguages.some((lang) => lang === "enCa" || lang === "enUs")
      ) {
        mappedLocales.add("en");
      }

      // Always include "fr" if any French variant is supported
      if (supportedLanguages.some((lang) => lang === "fr" || lang === "frCa")) {
        mappedLocales.add("fr");
      }

      filteredLocales = Array.from(mappedLocales).sort();

      // Filter locale names to only include filtered locales
      filteredLocaleNames = Object.fromEntries(
        Object.entries(SURVEYJS_LOCALE_NAMES).filter(([locale]) =>
          filteredLocales.includes(locale),
        ),
      ) as typeof SURVEYJS_LOCALE_NAMES;
    }

    surveyLocalization.supportedLocales = filteredLocales;
    surveyLocalization.localeNames = filteredLocaleNames;

    // Load survey content into the new creator immediately
    if (surveyJSObject) {
      if (typeof surveyJSObject === "string") {
        newCreator.text = surveyJSObject;
      } else {
        newCreator.text = JSON.stringify(surveyJSObject);
      }
    }

    // Add "Translate All Languages" toolbar action
    const translateAllAction = new Action({
      id: "translateAllLanguages",
      title: "Translate All Languages",
      tooltip: "Auto-translate to all supported languages",
      visible: false,
      action: async () => {
        const targetLocales = filteredLocales.filter((loc) => loc !== "en");
        if (targetLocales.length === 0) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const translation = (newCreator.getPlugin("translation") as any)?.model;
        if (!translation) return;

        const allItems = translation.root?.allLocItems || [];
        if (allItems.length === 0) return;

        const sourceStrings = allItems.map(
          (item: { getDefaultLocaleText: () => string }) =>
            item.getDefaultLocaleText() || "",
        );

        // Show progress
        const originalTitle = translateAllAction.title;
        translateAllAction.title = `Translating... (0/${targetLocales.length})`;
        translateAllAction.enabled = false;

        const requestData = sourceStrings.map((str: string) => ({ Text: str }));
        let completedCount = 0;

        // Translate all locales in parallel
        const results: {
          locale: string;
          translations: AzureTranslationResponse[];
        }[] = [];
        for (const locale of targetLocales) {
          try {
            const targetLocale = surveyJsLocaleToTranslateTarget(
              locale,
              supportedLanguages,
            );
            const response = await getSurveyTranslations({
              body: {
                source_strings: requestData,
                target_language: targetLocale,
              },
            });
            completedCount++;
            translateAllAction.title = `Translating... (${completedCount}/${targetLocales.length})`;
            results.push({
              locale,
              translations: response.data?.translations || [],
            });
          } catch (error) {
            console.error(`Error translating to ${locale}:`, error);
            completedCount++;
            translateAllAction.title = `Translating... (${completedCount}/${targetLocales.length})`;
            results.push({ locale, translations: [] });
          }
        }

        // Apply translations
        results.forEach(({ locale, translations }) => {
          translations.forEach(
            (result: { translations: { text: string }[] }, index: number) => {
              const item = allItems[index];
              if (item && result.translations?.[0]?.text) {
                item.setLocText(locale, result.translations[0].text);
              }
            },
          );
        });

        translateAllAction.title = originalTitle;
        translateAllAction.enabled = true;

        translation.reset();
        newCreator.doSave();
      },
    });

    // Add "Remove All Translations" toolbar action
    const removeTranslationsAction = new Action({
      id: "removeAllTranslations",
      title: "Remove All Translations",
      tooltip: "Remove all non-English translations",
      visible: false,
      action: function () {
        const surveyJSON = JSON.parse(newCreator.text);

        // Remove locale-related properties
        delete surveyJSON.locale;
        delete surveyJSON.locales;
        delete surveyJSON.supportedLocales;

        const isLocalizationObject = (
          obj: Record<string, unknown>,
        ): boolean => {
          const keys = Object.keys(obj);
          if (keys.length === 0) return false;
          // Has "default" or "en" key, OR all keys are 2-3 letter language codes
          const hasDefaultOrEn = "default" in obj || "en" in obj;
          const allKeysAreLangCodes = keys.every(
            (k) =>
              k === "default" ||
              (k.length >= 2 && k.length <= 3 && /^[a-z]+$/.test(k)),
          );
          return hasDefaultOrEn || allKeysAreLangCodes;
        };

        const getDefaultValue = (locObj: Record<string, unknown>): unknown => {
          return locObj["default"] ?? locObj["en"] ?? Object.values(locObj)[0];
        };

        const removeTranslations = (obj: Record<string, unknown>) => {
          if (!obj || typeof obj !== "object") return;

          for (const key of Object.keys(obj)) {
            const value = obj[key];

            if (Array.isArray(value)) {
              // Process array items, potentially simplifying choice items
              for (let i = 0; i < value.length; i++) {
                const item = value[i];
                if (item && typeof item === "object" && !Array.isArray(item)) {
                  const itemObj = item as Record<string, unknown>;

                  // Check if this is a choice item with value + localized text
                  if ("value" in itemObj && "text" in itemObj) {
                    const textVal = itemObj["text"];
                    if (
                      textVal &&
                      typeof textVal === "object" &&
                      !Array.isArray(textVal)
                    ) {
                      if (
                        isLocalizationObject(textVal as Record<string, unknown>)
                      ) {
                        // Simplify to just the value string
                        value[i] = itemObj["value"];
                        continue;
                      }
                    }
                  }

                  // Otherwise recurse
                  removeTranslations(itemObj);
                }
              }
            } else if (
              value &&
              typeof value === "object" &&
              !Array.isArray(value)
            ) {
              const locValue = value as Record<string, unknown>;

              if (isLocalizationObject(locValue)) {
                // Replace with default/en value
                obj[key] = getDefaultValue(locValue);
              } else {
                // Recurse into non-locale objects
                removeTranslations(locValue);
              }
            }
          }
        };

        removeTranslations(surveyJSON);

        newCreator.text = JSON.stringify(surveyJSON);
        newCreator.doSave();
      },
    });

    // JSON Editor toolbar actions
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let aceEditorInstance: any = null;
    let isWordWrap = false;

    const formatJsonAction = new Action({
      id: "jsonEditorFormat",
      title: "Format JSON",
      tooltip: "Auto-format and prettify JSON",
      visible: false,
      action: () => {
        if (!aceEditorInstance) return;
        try {
          const currentValue = aceEditorInstance.getValue();
          const parsed = JSON.parse(currentValue);
          const formatted = JSON.stringify(parsed, null, 2);
          aceEditorInstance.setValue(formatted, -1);
        } catch {
          console.warn("Cannot format invalid JSON");
        }
      },
    });

    const wordWrapAction = new Action({
      id: "jsonEditorWordWrap",
      title: "Word Wrap: Off",
      tooltip: "Toggle word wrap",
      visible: false,
      action: () => {
        if (!aceEditorInstance) return;
        isWordWrap = !isWordWrap;
        aceEditorInstance.getSession().setUseWrapMode(isWordWrap);
        wordWrapAction.title = isWordWrap ? "Word Wrap: On" : "Word Wrap: Off";
      },
    });

    // Add to toolbar
    newCreator.toolbar.actions.push(translateAllAction);
    newCreator.toolbar.actions.push(removeTranslationsAction);
    newCreator.toolbar.actions.push(formatJsonAction);
    newCreator.toolbar.actions.push(wordWrapAction);

    newCreator.onActiveTabChanged.add((_, options) => {
      const isTranslationTab = options.tabName === "translation";
      translateAllAction.visible = isTranslationTab;
      removeTranslationsAction.visible = isTranslationTab;

      // JSON Editor actions - tab name is "json"
      const isJsonTab = options.tabName === "json";
      formatJsonAction.visible = isJsonTab;
      wordWrapAction.visible = isJsonTab;

      // Get Ace editor instance when switching to JSON tab
      if (isJsonTab) {
        // timeout needed because editor is initialized after tab change event
        setTimeout(() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const editorPlugin = newCreator.getPlugin("json") as any;
          if (editorPlugin?.model?.aceEditor) {
            aceEditorInstance = editorPlugin.model.aceEditor;
          }
        }, 100);
      }

      // Track preview state for CSS wrapper
      const isPreviewTab =
        options.tabName === "test" || options.tabName === "preview";
      if (isPreviewTab && isCondensedForm) {
        setIsCondensedPreview(true);
      } else {
        setIsCondensedPreview(false);
      }
    });

    // Theme the preview survey. Without applyTheme, SurveyJS falls back to its
    // built-in teal palette; the live respondent-facing form uses defaultTheme
    // (blue), so the preview must apply a theme to match what respondents see.
    newCreator.onPreviewSurveyCreated.add((_, options) => {
      if (isCondensedForm) {
        options.survey.applyTheme(condensedViewTheme);
        options.survey.showNavigationButtons = false;
        options.survey.showTitle = false;
        options.survey.logo = "";
      } else {
        options.survey.applyTheme(defaultTheme);
      }
    });

    // Register visual indicators for showIn property
    registerShowInVisualIndicators(
      newCreator,
      isCondensedForm ? "condensed" : "main",
    );

    registerRowTranslateAction(newCreator);
    registerAddAllLanguagesItem(newCreator);

    setCreator(newCreator);

    return () => {
      newCreator.dispose();
    };
  }, [mode, isCondensedForm, supportedLanguages, surveyJSObject]);

  // Don't render until creator is initialized
  if (!creator) {
    return <div>Loading survey creator...</div>;
  }

  return (
    <div
      className={
        isCondensedPreview
          ? `condensed-report-wrapper ${styles.editorPreviewContainer}`
          : ""
      }
      style={{ height: "100%" }}
    >
      <SurveyCreatorComponent creator={creator} style={{ height: "100%" }} />
    </div>
  );
};

export default FormBuilder;
